import type { RichSatellite, Satellite, SatelliteClass, SatelliteKind, SatellitePositionRaw, SatelliteRegime } from './types';

const STATION_NORAD = new Set(['25544', '48274', '54216', '61784']);

const OBJECT_ID_COUNTRY: Record<string, string> = {
  '1998-067': 'United States / Russia / partners',
  '2021-035': 'China',
  '2004-049': 'France',
  '2011-076': 'United States',
  '2012-006': 'United States',
};

export function periodMinFromMeanMotion(meanMotion: number): number {
  return !meanMotion || meanMotion <= 0 ? 0 : 1440 / meanMotion;
}

export function regimeFromOrbit(periodMin: number, eccentricity: number): SatelliteRegime {
  if (periodMin <= 0) return 'LEO';
  if (eccentricity > 0.6 && periodMin > 600) return 'deep-space';
  if (periodMin >= 1400 && periodMin <= 1465) return 'GEO';
  if (periodMin > 1465) return 'deep-space';
  if (periodMin >= 200) return 'MEO';
  return 'LEO';
}

export function ownerCountryFromObject(objectId: string, name: string): string | null {
  const prefix = objectId.split('-').slice(0, 2).join('-');
  if (OBJECT_ID_COUNTRY[prefix]) return OBJECT_ID_COUNTRY[prefix];
  const upper = name.toUpperCase();
  if (/\b(USA|NROL|GPS|GOES|LANDSAT|TERRA|AQUA|NOAA)\b/.test(upper)) return 'United States';
  if (/\b(COSMOS|GLONASS|METEOR|MOLNIYA)\b/.test(upper)) return 'Russia';
  if (/\b(GALILEO|SENTINEL|METOP|ENVISAT)\b/.test(upper)) return 'Europe';
  if (/\b(BEIDOU|TIANGONG|YAOGAN|FENGYUN)\b/.test(upper)) return 'China';
  if (/\b(HIMawari|ALOS)\b/.test(upper)) return 'Japan';
  if (/\b(STARLINK|SPACEX)\b/.test(upper)) return 'United States';
  if (/\b(ONEWEB|IRIDIUM)\b/.test(upper)) return 'United Kingdom / United States';
  return null;
}

export function classifySatelliteKind(sat: Satellite): SatelliteKind {
  const name = (sat.name ?? '').toUpperCase();
  const periodMin = periodMinFromMeanMotion(sat.meanMotion);
  const regime = regimeFromOrbit(periodMin, sat.eccentricity);
  const objectType = (sat.objectType ?? '').toUpperCase();

  if (STATION_NORAD.has(sat.noradId) || /\b(ISS|ZARYA|TIANGONG|CSS)\b/.test(name)) return 'station';
  if (/\bSTARLINK\b/.test(name)) return 'starlink';
  if (
    /\b(GPS|NAVSTAR|GLONASS|GALILEO|BEIDOU|COMPASS|QZSS|IRNSS|NAVIC)\b/.test(name) ||
    (regime === 'MEO' && periodMin > 400 && periodMin < 900)
  ) {
    return 'navigation';
  }
  if (
    /\b(NROL|USA \d|USA-\d|SBIRS|MISTY|LACROSSE|KH-|KEYHOLE)\b/.test(name) ||
    (sat.classification === 'C' && regime !== 'GEO')
  ) {
    return 'military';
  }
  if (
    regime === 'GEO' &&
    /\b(INTELSAT|SES-|EUTELSAT|INMARSAT|ASTRA|DIRECTV|SKY|HISPASAT|THAICOM|OPTUS|GALAXY)\b/.test(name)
  ) {
    return 'geocomms';
  }
  if (/\b(ONEWEB|IRIDIUM|ORBCOMM|GLOBALSTAR|SWARM|KUIPER|ORBCOMM)\b/.test(name)) return 'constellation';
  if (objectType.includes('DEBRIS') || /\b(DEB|R\/B|ROCKET BODY)\b/.test(name) || name.startsWith('OBJECT ')) {
    return 'debris';
  }
  if (/\b(NASA|NOAA|LANDSAT|SENTINEL|TERRA|AQUA|GOES|METOP|HUBBLE|TESS|SWIFT|FENGYUN|YAOGAN|COSMO)\b/.test(name)) {
    return 'agency';
  }
  if (regime === 'GEO') return 'geocomms';
  if (regime === 'MEO') return 'navigation';
  if (regime === 'deep-space') return 'agency';
  return 'constellation';
}

export function satelliteClassFromKind(kind: SatelliteKind): SatelliteClass {
  if (kind === 'military') return 'military';
  if (kind === 'debris') return 'unknown';
  if (kind === 'starlink' || kind === 'constellation' || kind === 'geocomms') return 'commercial';
  return 'government';
}

function epochAgeDays(epoch: string): number {
  const ms = Date.parse(epoch.replace(' ', 'T') + 'Z');
  return Number.isFinite(ms) ? Math.max(0, (Date.now() - ms) / 86400000) : 0;
}

export function enrichSatellite(sat: Satellite, position: SatellitePositionRaw): RichSatellite {
  const periodMin = periodMinFromMeanMotion(sat.meanMotion);
  const kind = classifySatelliteKind(sat);
  return {
    ...sat,
    kind,
    satelliteClass: satelliteClassFromKind(kind),
    regime: regimeFromOrbit(periodMin, sat.eccentricity),
    altitudeKm: position.altKm,
    speedKmS: position.speedKmS,
    inclinationDeg: sat.inclination,
    periodMin,
    ageDays: epochAgeDays(sat.epoch),
    ownerCountry: ownerCountryFromObject(sat.objectId, sat.name),
    position,
  };
}

export function enrichSatellites(catalog: Satellite[], positions: Map<string, SatellitePositionRaw>): RichSatellite[] {
  const out = [];
  for (const sat of catalog) {
    const pos = positions.get(sat.noradId);
    if (pos) out.push(enrichSatellite(sat, pos));
  }
  return out;
}

export const SATELLITE_KIND_LABELS: Record<SatelliteKind, string> = {
  starlink: 'Starlink',
  station: 'Space station',
  navigation: 'Navigation',
  agency: 'Agency / science',
  military: 'Military',
  geocomms: 'GEO comms',
  constellation: 'LEO constellation',
  debris: 'Debris',
};

export const SATELLITE_CLASS_LABELS: Record<SatelliteClass, string> = {
  commercial: 'Commercial',
  government: 'Government',
  military: 'Military',
  unknown: 'Unknown',
};
